// src/pages/admin/AdminStudents.tsx
// Route: /admin/students

import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  PageHeader,
  Skeleton,
  EmptyState,
  StatCard,
  TableContainer,
  Th,
  Td,
  Avatar,
  Pagination,
  SearchInput,
  Select,
  Button,
  TrackChip,
  StudentChip,
  EnrolChip,
  PayChip,
  BillingChip,
  formatNaira,
  formatDate,
  trackConfig,
} from "./AdminShared";
import { AdminStudent, useAdminGetStudentsQuery } from "../../app/api/adminApi";

import { ArrowRight, Circle, Play, Sparkles, Users } from "lucide-react";
/* ─── STUDENT DETAIL DRAWER ──────────────────────────────────────── */

function StudentDrawer({
  student,
  onClose,
}: {
  student: AdminStudent;
  onClose: () => void;
}) {
  const tc = trackConfig(student.track);
  const enr = student.activeEnrolment;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-[#16213e]/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white shadow-[-8px_0_40px_rgba(27,45,94,0.12)] sm:max-w-[440px]"
        role="dialog"
        aria-modal
      >
        <div className="flex flex-shrink-0 items-center justify-between border-b border-[#ebeff7] px-6 py-5">
          <div>
            <h2 className="text-lg font-bold text-[#16213e]">
              {student.firstName} {student.lastName}
            </h2>
            <p className="mt-0.5 text-xs text-[#8189a0]">
              Age {student.age} · {tc.label}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-xl text-[#8189a0] hover:bg-[#f1f3f8]"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Status badges */}
          <div className="flex flex-wrap gap-2">
            <StudentChip status={student.studentStatus} />
            <TrackChip track={student.track} />
          </div>

          {/* Identity */}
          <div className="rounded-[18px] border border-[#dde2ec] overflow-hidden">
            <div className="bg-[#f9fafc] px-4 py-2.5 border-b border-[#ebeff7]">
              <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                Student details
              </p>
            </div>
            <div className="divide-y divide-[#f7f8fb] text-xs">
              {[
                {
                  label: "Date of birth",
                  value: formatDate(student.dateOfBirth),
                },
                { label: "Email", value: student.email ?? "Not provided" },
                { label: "Added", value: formatDate(student.createdAt) },
              ].map((r) => (
                <div key={r.label} className="flex justify-between px-4 py-2.5">
                  <span className="text-[#8189a0]">{r.label}</span>
                  <span className="font-semibold text-[#16213e]">
                    {r.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Parent */}
          {student.parent && (
            <div className="rounded-[18px] border border-[#dde2ec] overflow-hidden">
              <div className="bg-[#f9fafc] px-4 py-2.5 border-b border-[#ebeff7]">
                <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                  Parent
                </p>
              </div>
              <div className="px-4 py-3 flex items-center gap-3">
                <Avatar
                  firstName={student.parent.firstName}
                  lastName={student.parent.lastName}
                />
                <div>
                  <p className="text-sm font-semibold text-[#16213e]">
                    {student.parent.firstName} {student.parent.lastName}
                  </p>
                  <p className="text-xs text-[#8189a0]">
                    {student.parent.email}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Active enrolment */}
          {enr ? (
            <div className="rounded-[18px] border border-[#dde2ec] overflow-hidden">
              <div className="bg-[#f9fafc] px-4 py-2.5 border-b border-[#ebeff7] flex items-center justify-between">
                <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                  Active enrolment
                </p>
                <div className="flex gap-1.5">
                  <EnrolChip status={enr.status} />
                  <PayChip status={enr.paymentStatus} />
                </div>
              </div>
              <div className="divide-y divide-[#f7f8fb] text-xs">
                {[
                  {
                    label: "Quarter",
                    value: enr.quarter + " · " + enr.academicYear,
                  },
                  { label: "Plan", value: enr.billingLabel ?? "—" },
                  { label: "Fee", value: formatNaira(enr.feeAmount) },
                  {
                    label: "Progress",
                    value: `Week ${enr.currentWeek} · ${enr.progressPercent}%`,
                  },
                  { label: "Next payment", value: enr.nextPaymentLabel ?? "—" },
                ].map((r) => (
                  <div
                    key={r.label}
                    className="flex justify-between px-4 py-2.5"
                  >
                    <span className="text-[#8189a0]">{r.label}</span>
                    <span className="font-semibold text-[#16213e]">
                      {r.value}
                    </span>
                  </div>
                ))}
              </div>
              {/* Progress bar */}
              <div className="px-4 pb-3 pt-2">
                <div className="h-2 overflow-hidden rounded-full bg-[#ebeff7]">
                  <div
                    className="h-full rounded-full bg-[#1b2d5e] transition-all"
                    style={{ width: `${enr.progressPercent}%` }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-[18px] border border-dashed border-[#cdd4e2] bg-[#f9fafc] px-4 py-6 text-center">
              <p className="text-sm text-[#8189a0]">No active enrolment</p>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 border-t border-[#ebeff7] px-6 py-5">
          <Link
            to={`/admin/progress?studentId=${student.id}`}
            className="flex w-full items-center justify-center gap-2 rounded-2xl border border-[#dde2ec] py-3 text-sm font-semibold text-[#3b4560] hover:bg-[#f1f3f8]"
          >
            View progress reports{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
          </Link>
        </div>
      </div>
    </>
  );
}

/* ─── TABLE ROW ──────────────────────────────────────────────────── */

function StudentRow({
  student,
  onClick,
}: {
  student: AdminStudent;
  onClick: () => void;
}) {
  const tc = trackConfig(student.track);
  const enr = student.activeEnrolment;
  return (
    <tr
      onClick={onClick}
      className="cursor-pointer border-b border-[#f7f8fb] transition hover:bg-[#f9fafc]"
    >
      <Td>
        <div className="flex items-center gap-2.5">
          <Avatar
            firstName={student.firstName}
            lastName={student.lastName}
            size="sm"
          />
          <div>
            <p className="font-semibold text-[#16213e]">
              {student.firstName} {student.lastName}
            </p>
            <p className="text-[12.5px] text-[#8189a0]">
              {student.email ?? "No email"}
            </p>
          </div>
        </div>
      </Td>
      <Td>
        <span className="text-xs font-medium text-[#5a6278]">
          Age {student.age}
        </span>
      </Td>
      <Td>
        <span
          className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold"
          style={{
            background: tc.bg,
            color: tc.text,
            border: `1px solid ${tc.border}`,
          }}
        >
          {tc.label}
        </span>
      </Td>
      <Td>
        <StudentChip status={student.studentStatus} />
      </Td>
      <Td>
        {enr ? (
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-medium">{enr.quarter}</span>
            <BillingChip period={enr.billingPeriod} label={enr.billingLabel} />
          </div>
        ) : (
          <span className="text-xs text-[#8189a0]">—</span>
        )}
      </Td>
      <Td>
        {enr ? (
          <div>
            <div className="mb-1 flex justify-between text-[11.5px]">
              <span className="text-[#8189a0]">Week {enr.currentWeek}</span>
              <span className="font-bold text-[#1b2d5e]">
                {enr.progressPercent}%
              </span>
            </div>
            <div className="h-1.5 w-24 overflow-hidden rounded-full bg-[#ebeff7]">
              <div
                className="h-full rounded-full bg-[#1b2d5e]"
                style={{ width: `${enr.progressPercent}%` }}
              />
            </div>
          </div>
        ) : (
          <span className="text-xs text-[#8189a0]">—</span>
        )}
      </Td>
      <Td>
        {enr ? (
          <PayChip status={enr.paymentStatus} />
        ) : (
          <span className="text-xs text-[#8189a0]">—</span>
        )}
      </Td>
      <Td>
        <svg className="h-4 w-4 text-[#c9cfe0]" viewBox="0 0 16 16" fill="none">
          <path
            d="M6 4l4 4-4 4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Td>
    </tr>
  );
}

/* ─── ROOT PAGE ──────────────────────────────────────────────────── */

export default function AdminStudents() {
  const [page, setPage] = useState(1);
  const [track, setTrack] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<AdminStudent | null>(null);

  const { data, isLoading, isError, refetch } = useAdminGetStudentsQuery({
    page,
    limit: 30,
    ...(track ? { track: track as any } : {}),
    ...(statusFilter ? { studentStatus: statusFilter as any } : {}),
    ...(search ? { search } : {}),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const students = data?.data ?? [];

  const stats = useMemo(
    () => ({
      total: data?.total ?? 0,
      active: students.filter((s) => s.studentStatus === "active").length,
      enrolled: students.filter((s) => s.studentStatus === "enrolled").length,
      prospect: students.filter((s) => s.studentStatus === "prospect").length,
    }),
    [students, data],
  );

  if (isError)
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3">
        <p className="text-sm font-semibold text-[#16213e]">
          Failed to load students
        </p>
        <Button variant="primary" onClick={refetch}>
          Retry
        </Button>
      </div>
    );

  return (
    <>
      <PageHeader
        title="Students"
        subtitle="All enrolled and prospective students across every programme track."
      />

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[72px] rounded-[20px]" />
            ))
          : [
              {
                label: "Total",
                value: String(stats.total),
                icon: Users,
                color: "#1b2d5e",
                bg: "#ebeff7",
                sub: "all students",
              },
              {
                label: "Active",
                value: String(stats.active),
                icon: Play,
                color: "#6cc2a0",
                bg: "#edfaf4",
                sub: "in sessions",
              },
              {
                label: "Enrolled",
                value: String(stats.enrolled),
                icon: Sparkles,
                color: "#4a44b0",
                bg: "#e8ecf5",
                sub: "confirmed",
              },
              {
                label: "Prospects",
                value: String(stats.prospect),
                icon: Circle,
                color: "#f0a050",
                bg: "#fff8ee",
                sub: "not yet paid",
              },
            ].map((s) => <StatCard key={s.label} {...s} />)}
      </div>

      {/* Filters */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          placeholder="Search by name or email…"
        />
        <Select
          value={track}
          onChange={(v) => {
            setTrack(v);
            setPage(1);
          }}
        >
          <option value="">All tracks</option>
          <option value="foundation_builders">Foundation (6–9)</option>
          <option value="explorers">Explorers (9–12)</option>
          <option value="innovators">Innovators (13–18)</option>
        </Select>
        <Select
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          <option value="prospect">Prospect</option>
          <option value="enrolled">Enrolled</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </Select>
        <p className="text-xs text-[#8189a0]">{students.length} shown</p>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-xl" />
          ))}
        </div>
      ) : students.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No students found"
          subtitle="Try adjusting the filters."
        />
      ) : (
        <TableContainer>
          <table className="w-full text-left text-sm">
            <thead className="border-b border-[#ebeff7]">
              <tr>
                {[
                  "Student",
                  "Age",
                  "Track",
                  "Status",
                  "Enrolment",
                  "Progress",
                  "Payment",
                  "",
                ].map((h) => (
                  <Th key={h}>{h}</Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {students.map((s) => (
                <StudentRow
                  key={s.id}
                  student={s}
                  onClick={() => setSelected(s)}
                />
              ))}
            </tbody>
          </table>
          <Pagination
            page={data?.page ?? 1}
            pages={data?.pages ?? 1}
            total={data?.total ?? 0}
            count={students.length}
            onPage={(p) => {
              setPage(p);
              window.scrollTo({ top: 0 });
            }}
          />
        </TableContainer>
      )}

      {selected && (
        <StudentDrawer student={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
}
